import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useCurrency } from '../../context/CurrencyContext';
import { formatAmount } from '../../lib/currencyUtils';
import {
  ArrowLeft, Download, UserPlus, Store, MapPin, Phone, Mail,
  Users, Ticket, DollarSign, Calendar, X, Check, Clock
} from 'lucide-react';
import api from '../../api/client';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const TAB_LIST = ['overview', 'redemptions', 'users', 'settlements'];
const TAB_LABELS = { overview: 'Overview', redemptions: 'Redemptions', users: 'Users', settlements: 'Settlements' };

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-heading text-lg font-bold text-[#561D1A]">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function StoreDetailView({ storeId, onBack }) {
  const { currency: activeCurrency, formatDateTime: formatDate, formatShortDate: formatShort } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', phone: '' });
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/store/admin/stores/${storeId}/detail`);
      setData(res.data);
    } catch {
      toast.error('Failed to load store details');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [storeId]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await api.post(`/api/store/admin/stores/${storeId}/invite-user`, inviteForm);
      toast.success(`User ${res.data.name} invited${res.data.email_sent ? ' (email sent)' : ''}`);
      if (res.data.temp_password) {
        toast.info(`Temporary password: ${res.data.temp_password}`, { duration: 10000 });
      }
      setInviteModal(false);
      setInviteForm({ name: '', email: '', phone: '' });
      load();
    } catch (err) {
      toast.error(err.displayMessage || 'Failed to invite');
    } finally { setInviting(false); }
  };

  const downloadReport = async () => {
    try {
      const res = await api.get(`/api/store/admin/stores/${storeId}/report`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `store_report_${data?.store?.name?.replace(/\s+/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded');
    } catch { toast.error('Download failed'); }
  };

  if (loading) return (
    <div className="space-y-4">
      <div className="h-8 w-24 bg-gray-100 rounded animate-pulse" />
      <div className="h-48 bg-gray-100 rounded-2xl animate-pulse" />
      <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
    </div>
  );

  if (!data) return (
    <div className="text-center py-16 text-gray-400">
      <p>Store not found</p>
      <button onClick={onBack} className="mt-3 text-sm text-[#561D1A] hover:underline">Go back</button>
    </div>
  );

  const { store, kpis, recent_redemptions, users, settlements } = data;
  const statusColor = store.status === 'active' ? 'text-emerald-600 bg-emerald-50' :
    store.status === 'pilot' ? 'text-blue-600 bg-blue-50' : 'text-gray-500 bg-gray-100';

  return (
    <div data-testid="store-detail">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#561D1A] mb-4" data-testid="back-to-stores">
        <ArrowLeft size={15} /> Back to Stores
      </button>

      {/* Store Header */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            {store.logo_url ? (
              <img src={store.logo_url.startsWith('http') ? store.logo_url : `${API_BASE}${store.logo_url}`}
                alt="" className="w-14 h-14 rounded-xl object-cover border border-gray-100" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-[#561D1A]/10 flex items-center justify-center">
                <Store size={24} className="text-[#561D1A]" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-heading text-xl font-bold text-[#561D1A]">{store.name}</h2>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusColor}`}>
                  {(store.status || 'active').charAt(0).toUpperCase() + (store.status || 'active').slice(1)}
                </span>
              </div>
              <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                {store.category && <span>{store.category}</span>}
                {(store.location_floor || store.location_unit) && (
                  <span className="flex items-center gap-1"><MapPin size={10} /> {[store.location_floor, store.location_unit].filter(Boolean).join(', ')}</span>
                )}
                {store.contact_email && <span className="flex items-center gap-1"><Mail size={10} /> {store.contact_email}</span>}
                {store.contact_phone && <span className="flex items-center gap-1"><Phone size={10} /> {store.contact_phone}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={downloadReport} className="flex items-center gap-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-xl font-medium" data-testid="download-report-btn">
              <Download size={13} /> Report
            </button>
            <button onClick={() => { setInviteForm({ name: '', email: '', phone: '' }); setInviteModal(true); }}
              className="flex items-center gap-1.5 text-xs bg-[#561D1A] text-white px-3 py-2 rounded-xl font-medium hover:bg-[#3d1413]" data-testid="invite-user-btn">
              <UserPlus size={13} /> Invite User
            </button>
          </div>
        </div>

        {/* Settlement Profile */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-6 text-xs text-gray-500">
          <span>Settlement: <strong className="text-gray-700 capitalize">{store.settlement_frequency || 'Monthly'}</strong></span>
          <span>Commission: <strong className="text-gray-700">{store.commission_percent || 0}%</strong></span>
          {store.contact_person && <span>Contact: <strong className="text-gray-700">{store.contact_person}</strong></span>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 w-fit" data-testid="store-tabs">
        {TAB_LIST.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === t ? 'bg-white text-[#561D1A] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${t}`}>
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Total Redeemed</p>
              <p className="font-heading text-xl font-bold text-[#561D1A]">{formatAmount(kpis.total_redeemed || 0, activeCurrency)}</p>
              <p className="text-[10px] text-gray-400 mt-1">{kpis.redemption_count || 0} transactions</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Last Settlement</p>
              <p className="font-heading text-xl font-bold text-emerald-600">{formatAmount(kpis.last_settlement_amount || 0, activeCurrency)}</p>
              <p className="text-[10px] text-gray-400 mt-1">{formatShort(kpis.last_settlement_date)}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Store Users</p>
              <p className="font-heading text-xl font-bold text-gray-800">{users?.length || 0}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Settlements</p>
              <p className="font-heading text-xl font-bold text-gray-800">{settlements?.length || 0}</p>
            </div>
          </div>
        </div>
      )}

      {tab === 'redemptions' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gray-50/80 border-b border-gray-100">
              <tr>
                {['Date', 'Voucher', 'Amount', 'Remaining', 'Type', 'Cashier'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(!recent_redemptions || recent_redemptions.length === 0) ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm">No redemptions yet</td></tr>
              ) : recent_redemptions.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50/60" data-testid="redemption-row">
                  <td className="px-4 py-3 text-xs text-gray-500">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3"><span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{r.voucher_code}</span></td>
                  <td className="px-4 py-3 font-bold text-[#561D1A] text-sm">{formatAmount(r.amount, activeCurrency)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{formatAmount(r.remaining_balance, activeCurrency)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${r.is_partial ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-600'}`}>{r.is_partial ? 'Partial' : 'Full'}</span></td>
                  <td className="px-4 py-3 text-xs text-gray-500">{r.merchant_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'users' && (
        <div className="space-y-3">
          {(!users || users.length === 0) ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center text-gray-400">
              <Users size={32} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No store users yet</p>
              <button onClick={() => setInviteModal(true)} className="mt-3 text-xs text-[#561D1A] hover:underline">Invite a user</button>
            </div>
          ) : users.map(u => (
            <div key={u.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center justify-between" data-testid="store-user-row">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#561D1A]/10 flex items-center justify-center text-xs font-bold text-[#561D1A]">
                  {u.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{u.name}</p>
                  <p className="text-xs text-gray-400">{u.email} {u.phone ? `| ${u.phone}` : ''}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 font-medium capitalize">{u.role}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${u.is_active ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                  {u.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'settlements' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
          <Clock size={40} className="mx-auto mb-3 text-gray-300" />
          <h3 className="text-base font-bold text-gray-800 mb-1">Settlement History</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">Settlement batches will appear here once M-Pesa integration is live and settlement runs are processed.</p>
          {settlements?.length > 0 && (
            <div className="mt-4 space-y-2">
              {settlements.map((s, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-3 text-sm text-left">
                  <span className="font-medium">{formatAmount(s.amount, activeCurrency)}</span> — {formatShort(s.settled_at)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Invite User Modal */}
      {inviteModal && (
        <Modal title={`Invite User — ${store.name}`} onClose={() => setInviteModal(false)}>
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Full Name *</label>
              <input required value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="invite-name" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Email *</label>
              <input required type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" data-testid="invite-email" />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Phone</label>
              <input value={inviteForm.phone} onChange={e => setInviteForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full border-b-2 border-gray-200 focus:border-[#561D1A] py-2 text-sm outline-none bg-transparent" />
            </div>
            <p className="text-xs text-gray-400">The user will be assigned as a <strong>Merchant</strong> for this store with a temporary password.</p>
            <button type="submit" disabled={inviting}
              className="w-full bg-[#561D1A] text-white py-3 rounded-full text-sm font-medium hover:bg-[#3d1413] disabled:opacity-50" data-testid="send-invite-btn">
              {inviting ? 'Sending...' : 'Send Invite'}
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
